/**
 * Order Number Generator Utility
 * 
 * Generates order numbers based on user-configurable formats.
 * Supports date-based and sequential numbering.
 */

import { format } from "date-fns";
import type { OrderIdConfig, OrderIdFormat } from "@/hooks/useUserSettings";

export type OrderSource = 'manual' | 'external' | 'ai' | 'courier';

/**
 * Format date according to the specified format
 */
export function formatOrderDate(date: Date, dateFormat: 'YYMMDD' | 'DDMMYY' | 'none'): string {
  switch (dateFormat) {
    case 'YYMMDD':
      return format(date, 'yyMMdd');
    case 'DDMMYY':
      return format(date, 'ddMMyy');
    case 'none':
      return '';
    default:
      return '';
  }
}

/**
 * Get prefix for a given source based on config mode
 */
function getSourcePrefix(config: OrderIdConfig, source: OrderSource): string {
  if (config.mode === 'per-source' && config.perSourceFormats) {
    return config.perSourceFormats[source]?.prefix || '';
  }
  return config.unifiedFormat.prefix;
}

/**
 * Build order number from config, source, and sequence
 */
export function buildOrderNumber(
  config: OrderIdConfig,
  source: OrderSource,
  sequence: number,
  date: Date = new Date()
): string {
  const fmt = config.unifiedFormat;
  const prefix = getSourcePrefix(config, source);
  const dateStr = formatOrderDate(date, fmt.dateFormat);
  const seqStr = String(sequence).padStart(fmt.sequenceLength, '0');
  
  const parts: string[] = [];
  if (prefix) parts.push(prefix);
  if (dateStr) parts.push(dateStr);
  parts.push(seqStr);
  
  return parts.join(fmt.separator);
}

/**
 * Get a preview of the order number format (using sequence 1)
 */
export function getOrderIdPreview(
  config: OrderIdConfig,
  source: OrderSource = 'manual'
): string {
  return buildOrderNumber(config, source, 1);
}

/**
 * Parse existing order number to extract sequence (for counting)
 * Returns the numeric sequence part if pattern matches, null otherwise
 */
export function extractSequenceFromOrderNumber(
  orderNumber: string,
  config: OrderIdConfig,
  date: Date = new Date()
): number | null {
  const fmt = config.unifiedFormat;
  const dateStr = formatOrderDate(date, fmt.dateFormat);
  
  // Build regex pattern to match the order number format
  // For example: ORD-241227-0001 should match and extract 0001
  let pattern = '';
  const sep = fmt.separator ? `\\${fmt.separator}` : '';
  
  // Prefix pattern (optional)
  pattern += `[A-Z]*`;
  if (sep) pattern += `${sep}?`;
  
  // Date pattern (if date format is used)
  if (fmt.dateFormat !== 'none') {
    pattern += dateStr;
    if (sep) pattern += sep;
  }
  
  // Sequence pattern
  pattern += `(\\d{${fmt.sequenceLength}})`;
  
  const regex = new RegExp(pattern);
  const match = orderNumber.match(regex);
  
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  
  return null;
}

/**
 * Get the next sequence number based on today's orders count
 */
export function getNextSequence(todayOrderCount: number): number {
  return todayOrderCount + 1;
}
